import { useCallback, useRef, useState } from 'react';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../state/zustand-store';
import { getAllDriveLabelsForManagerQuery } from '../../../api/users-api';
import { bulkDeleteDriveLabels } from '../../../api/drive-labels-api';
import { handleErrors } from '../../../utils/errors';
import { Messages } from '../../../utils/message-constants';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

interface UseDeleteLabelsOptions {
  changeLogRoles: string[];
  onDeleted: (labels: Label[]) => void;
}

/**
 * Deletes labels in one request and toasts the outcome. The confirmation dialog calls
 * `deleteLabels` once the user confirms, and it asks for a change log first when the role requires
 * one. Render `<ChangeLogDialog {...changeLogDialogProps} />` next to the caller.
 */
export function useDeleteLabels({
  changeLogRoles,
  onDeleted,
}: UseDeleteLabelsOptions) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [labelsAwaitingChangeLog, setLabelsAwaitingChangeLog] = useState<
    Label[]
  >([]);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  // Set the moment a request starts. `isPending` only flips after the next render, so two quick
  // clicks (a fieldless draft has no confirm dialog to disable) could both get through on it.
  const inFlightRef = useRef(false);

  const { mutate, isPending } = useMutation({
    mutationFn: (variables: { labels: Label[]; changeLogMessage: string }) =>
      bulkDeleteDriveLabels(
        variables.labels.map((label) => label.id ?? ''),
        variables.changeLogMessage
      ),
    onSuccess: (results, { labels }) => {
      const failures = results.filter((result) => !result.success);
      if (failures.length === 0) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.DriveLabels.DeleteSuccess(labels.length),
        });
      } else {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: Messages.DriveLabels.DeletePartial(
            results.length - failures.length,
            results.length,
            failures[0].error
          ),
        });
      }
      // Exact: the per-label lock and permission queries share this key as a prefix, and a prefix
      // match would refetch one request per row, including the labels that were just deleted.
      queryClient.invalidateQueries({
        queryKey: getAllDriveLabelsForManagerQuery().queryKey,
        exact: true,
      });
      setChangeLogMessage('');
      onDeleted(labels);
    },
    onError: (error, { labels }) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          labels.length === 1 ? 'label' : 'labels'
        ),
      });
    },
    onSettled: () => {
      inFlightRef.current = false;
      setLabelsAwaitingChangeLog([]);
    },
  });

  const start = useCallback(
    (labels: Label[], message: string) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      mutate({ labels, changeLogMessage: message });
    },
    [mutate]
  );

  const deleteLabels = useCallback(
    (labels: Label[]) => {
      if (
        isAbleToSeeUIElement(changeLogRoles, DriveRoles.DeleteDriveLabels.value)
      ) {
        setLabelsAwaitingChangeLog(labels);
      } else {
        start(labels, '');
      }
    },
    [changeLogRoles, start]
  );

  return {
    deleteLabels,
    isPending,
    changeLogDialogProps: {
      open: labelsAwaitingChangeLog.length > 0,
      handleClose: () => setLabelsAwaitingChangeLog([]),
      changeLogMessage,
      setChangeLogMessage,
      mutate: () => start(labelsAwaitingChangeLog, changeLogMessage),
      mutateLoading: isPending,
    },
  };
}
